/**
 * Hook for checking module access permissions
 * Combines admin status, tenant module subscriptions, and per-user permissions.
 *
 * Priority chain:
 * 1. Super admins → see all modules
 * 2. tenant_modules → tenant-level enabled/disabled
 * 3. user_module_access → per-user overrides (via get_user_module_access RPC)
 *
 * Falls back to showing all modules if no tenant_modules rows exist
 * (backwards-compatible for tenants without module config).
 * Falls back to tenant defaults if no user_module_access rows exist.
 */

import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePlatformAdmin } from './usePlatformAdmin';
import { usePlatformTenant } from '../contexts/PlatformTenantContext';
import { useAuth } from './useAuth';
import { supabase } from '../integrations/supabase/client';
import { 
  ACTIVE_MODULES, 
  COMING_SOON_MODULES, 
  getCurrentModule,
  type ModuleDefinition 
} from '../config/modules';

interface UserModuleAccess {
  module_name: string;
  tenant_enabled: boolean;
  user_enabled: boolean;
  has_override: boolean;
}

export interface ModuleAccess {
  /** All modules the user can access */
  accessibleModules: ModuleDefinition[];
  /** Coming soon modules to display */
  comingSoonModules: ModuleDefinition[];
  /** Currently active module based on route */
  currentModule: ModuleDefinition | undefined;
  /** Check if user can access a specific module */
  canAccess: (moduleId: string) => boolean;
  /** Whether the access check is still loading */
  loading: boolean;
}

export const useModuleAccess = (): ModuleAccess => {
  const { hasAdminAccess, loading: adminLoading } = usePlatformAdmin();
  const { platformTenant, isSuperAdmin } = usePlatformTenant();
  const { user } = useAuth();
  const location = useLocation();
  const [enabledModuleIds, setEnabledModuleIds] = useState<Set<string> | null>(null);
  const [userModuleMap, setUserModuleMap] = useState<Map<string, boolean> | null>(null);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [userModulesLoading, setUserModulesLoading] = useState(false);

  // Fetch tenant module subscriptions
  useEffect(() => {
    if (!platformTenant?.id) {
      setEnabledModuleIds(null);
      return;
    }

    let cancelled = false;
    setModulesLoading(true);

    supabase
      .from('tenant_modules')
      .select('module_name, is_enabled')
      .eq('platform_tenant_id', platformTenant.id)
      .eq('is_installed', true)
      .eq('is_enabled', true)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data || data.length === 0) {
          // No module config → show all modules (backwards compatible)
          setEnabledModuleIds(null);
        } else {
          setEnabledModuleIds(new Set(data.map(m => m.module_name)));
        }
        setModulesLoading(false);
      });

    return () => { cancelled = true; };
  }, [platformTenant?.id]);

  // Fetch per-user module access (merged with tenant defaults)
  useEffect(() => {
    if (!user?.id || isSuperAdmin) {
      setUserModuleMap(null);
      return;
    }

    let cancelled = false;
    setUserModulesLoading(true);

    (supabase as any)
      .rpc('get_user_module_access', { _user_id: user.id })
      .then(({ data, error }: { data: UserModuleAccess[] | null; error: any }) => {
        if (cancelled) return;
        if (error || !data || data.length === 0) {
          // No per-user config → fall back to tenant defaults
          setUserModuleMap(null);
        } else {
          const map = new Map<string, boolean>();
          data.forEach(m => map.set(m.module_name, m.user_enabled));
          setUserModuleMap(map);
        }
        setUserModulesLoading(false);
      });

    return () => { cancelled = true; };
  }, [user?.id, isSuperAdmin]);

  const loading = adminLoading || modulesLoading || userModulesLoading;

  const accessibleModules = useMemo(() => {
    return ACTIVE_MODULES.filter(module => {
      // Super admins can see all modules regardless of any config
      if (isSuperAdmin) return true;

      // Per-user module access takes highest priority (already merged with tenant defaults).
      // This overrides requiresAdmin — if a user is explicitly granted access, they see it.
      if (userModuleMap !== null) {
        const userEnabled = userModuleMap.get(module.id);
        // If the module is in the map, use the user-level value
        if (userEnabled !== undefined) return userEnabled;
        // If module not in map (not in tenant_modules), fall through to default checks
      }

      // If module requires admin and no per-user override granted it, check admin access
      if (module.requiresAdmin && !hasAdminAccess) {
        return false;
      }

      // If no per-user config, fall back to tenant-level check
      // null means no config exists → show all (backwards compatible)
      if (enabledModuleIds !== null && !enabledModuleIds.has(module.id)) {
        return false;
      }

      return true;
    });
  }, [hasAdminAccess, isSuperAdmin, enabledModuleIds, userModuleMap]);

  const currentModule = useMemo(() => {
    return getCurrentModule(location.pathname);
  }, [location.pathname]);

  const canAccess = (moduleId: string): boolean => {
    return accessibleModules.some(m => m.id === moduleId);
  };

  return {
    accessibleModules,
    comingSoonModules: COMING_SOON_MODULES,
    currentModule,
    canAccess,
    loading,
  };
};
